import type { PostSortRowsParams } from 'ag-grid-community'

import { hasDraftFlag, hasRowId, isNewRowId } from './newRow'
import { isRecord } from '@/utils/typeGuards'

type PostSortRowsOptions<Row> = {
  isNewRow?: (row: Row | undefined) => boolean
  isDraftRow?: (row: Row | undefined) => boolean
  getInsertAfterRowId?: (row: Row | undefined) => string | undefined
  getRowId?: (row: Row | undefined) => string | undefined
}

const defaultIsNewRow = <Row>(row: Row | undefined): boolean => {
  if (!hasRowId(row)) return false
  return isNewRowId(row.id)
}

const defaultIsDraftRow = <Row>(row: Row | undefined): boolean => {
  return hasDraftFlag(row) ? row.draft : false
}

const defaultGetInsertAfterRowId = <Row>(
  row: Row | undefined,
): string | undefined => {
  if (!isRecord(row)) return undefined
  const insertAfterRowId = row.insertAfterRowId
  if (typeof insertAfterRowId !== 'string' || insertAfterRowId.length === 0) {
    return undefined
  }
  return insertAfterRowId
}

const defaultGetRowId = <Row>(row: Row | undefined): string | undefined => {
  if (!hasRowId(row)) return undefined
  if (typeof row.id !== 'string' || row.id.length === 0) return undefined
  return row.id
}

export const postSortRowsPinNewRows = <Row>(
  params: PostSortRowsParams<Row>,
  options: PostSortRowsOptions<Row> = {},
): void => {
  const isNewRow = options.isNewRow ?? defaultIsNewRow
  const isDraftRow = options.isDraftRow ?? defaultIsDraftRow
  const getInsertAfterRowId =
    options.getInsertAfterRowId ?? defaultGetInsertAfterRowId
  const getRowId = options.getRowId ?? defaultGetRowId

  const rowNodes = params.nodes
  let nextInsertPos = 0

  for (let i = 0; i < rowNodes.length; i++) {
    const data = rowNodes[i].data
    if (!isNewRow(data) || isDraftRow(data)) {
      rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0])
      nextInsertPos++
    }
  }

  const anchoredDraftNodes = rowNodes.flatMap((node) => {
    const data = node.data
    if (!isNewRow(data) || !isDraftRow(data)) return []

    const anchorRowId = getInsertAfterRowId(data)
    if (!anchorRowId) return []

    return [{ node, anchorRowId }]
  })

  anchoredDraftNodes.forEach(({ node, anchorRowId }) => {
    const currentIndex = rowNodes.indexOf(node)
    if (currentIndex < 0) return

    const anchorIndex = rowNodes.findIndex(
      (candidateNode) => getRowId(candidateNode.data) === anchorRowId,
    )
    if (anchorIndex < 0) return

    const targetIndex = anchorIndex + 1
    const [movedNode] = rowNodes.splice(currentIndex, 1)
    const adjustedTargetIndex =
      currentIndex < targetIndex ? targetIndex - 1 : targetIndex
    rowNodes.splice(adjustedTargetIndex, 0, movedNode)
  })
}
